import dayjs from "dayjs"

import { parseActivityMoment } from "../../../../../Lead/LeadWorkspacePage/activity/lead-activity-date.ts"
import type { AuditActor, AuditLogEntry } from "../../../../../../types/audit-log.types.ts"
import { normalizeBackendChanges } from "../../../../../../utils/audit-log/normalize-backend-changes.ts"
import type { ProjectLogFilters, ProjectLogOption } from "./project-logs.types.ts"

const NO_LOG_FILTERS: ProjectLogFilters = {
  search: "",
  action: "all",
  type: "all",
  user: null,
  after: null,
  before: null
}

const humanizeToken = (value: string): string =>
  value
    .replace(/[_-]+/g, " ")
    .trim()
    .replace(/\b\w/g, (c) => c.toUpperCase())

interface ResolvedActor {
  id: string | null
  label: string
  avatar?: string
}

const SYSTEM_ACTOR: ResolvedActor = { id: null, label: "System" }

/** Mirrors the `AuditLogHistory` actor line: object actor, then `actor_name`, then a bare id. */
const resolveLogActor = (entry: AuditLogEntry): ResolvedActor => {
  const raw = entry.actor
  if (raw && typeof raw === "object" && !Array.isArray(raw)) {
    const actor = raw as AuditActor
    const label = actor.full_name?.trim() || actor.email?.trim()
    if (label)
      return {
        id: actor.id != null ? String(actor.id) : label,
        label,
        avatar: actor.avatar || undefined
      }
  }

  const name = entry.actor_name?.trim()
  if (name) return { id: name, label: name }

  if (raw == null || raw === "") return SYSTEM_ACTOR

  return { id: String(raw), label: String(raw) }
}

const changesSearchText = (changes: unknown): string => {
  const normalized = normalizeBackendChanges(changes)

  if (normalized && typeof normalized === "object" && !Array.isArray(normalized)) {
    return Object.entries(normalized as Record<string, unknown>)
      .map(([key, value]) => {
        if (value && typeof value === "object" && "from" in value && "to" in value) {
          const { from, to } = value as { from: unknown; to: unknown }
          return `${key} ${String(from ?? "")} ${String(to ?? "")}`
        }
        return `${key} ${JSON.stringify(value)}`
      })
      .join(" ")
  }

  if (typeof changes === "string") return changes

  try {
    return JSON.stringify(changes ?? "")
  } catch {
    return ""
  }
}

const searchHaystack = (entry: AuditLogEntry): string =>
  [
    entry.action,
    entry.type,
    entry.target,
    resolveLogActor(entry).label,
    changesSearchText(entry.changes)
  ]
    .filter(Boolean)
    .join(" ")
    .toLowerCase()

/**
 * `entry.timestamp` arrives pre-formatted in the account's own date format
 * (e.g. `29/08/2026 20:45`), not ISO — parsing it with a bare `dayjs()` silently
 * produces an invalid/misread date on any non-ISO format, which breaks sorting
 * and date-range filtering without ever throwing. `parseActivityMoment` is the
 * same account-format-aware parser already proven for the Lead Activity tab.
 */
const parseLogTimestamp = (
  entry: AuditLogEntry,
  dateFormat?: string | null,
  timezone?: string | null
) => parseActivityMoment(entry.timestamp, dateFormat, timezone)

/** Newest first — an audit log is read from what just happened backwards. */
const sortLogsNewestFirst = (
  entries: AuditLogEntry[],
  dateFormat?: string | null,
  timezone?: string | null
): AuditLogEntry[] =>
  [...entries].sort(
    (a, b) =>
      (parseLogTimestamp(b, dateFormat, timezone)?.valueOf() ?? 0) -
      (parseLogTimestamp(a, dateFormat, timezone)?.valueOf() ?? 0)
  )

const filterProjectLogs = (
  entries: AuditLogEntry[],
  filters: ProjectLogFilters = NO_LOG_FILTERS,
  dateFormat?: string | null,
  timezone?: string | null
): AuditLogEntry[] =>
  entries.filter((entry) => {
    if (filters.action !== "all" && entry.action !== filters.action) return false
    if (filters.type !== "all" && (entry.type ?? "") !== filters.type) return false
    if (filters.user && resolveLogActor(entry).id !== filters.user) return false

    const at = parseLogTimestamp(entry, dateFormat, timezone)
    if (at) {
      if (filters.after && at.isBefore(dayjs(filters.after), "day")) return false
      if (filters.before && at.isAfter(dayjs(filters.before), "day")) return false
    }

    const query = filters.search.trim().toLowerCase()
    if (query && !searchHaystack(entry).includes(query)) return false

    return true
  })

/** Only the actions the loaded log rows actually carry — never an invented type. */
const projectLogActionOptions = (entries: AuditLogEntry[]): ProjectLogOption[] => {
  const seen = new Map<string, string>()
  entries.forEach((entry) => {
    const value = entry.action?.trim()
    if (value && !seen.has(value)) seen.set(value, humanizeToken(value))
  })
  return [...seen.entries()]
    .map(([value, label]) => ({ value, label }))
    .sort((a, b) => a.label.localeCompare(b.label))
}

/** Only the object kinds (`type`) the loaded log rows actually carry. */
const projectLogTypeOptions = (entries: AuditLogEntry[]): ProjectLogOption[] => {
  const seen = new Map<string, string>()
  entries.forEach((entry) => {
    const value = entry.type?.trim()
    if (value && !seen.has(value)) seen.set(value, humanizeToken(value))
  })
  return [...seen.entries()]
    .map(([value, label]) => ({ value, label }))
    .sort((a, b) => a.label.localeCompare(b.label))
}

/** Only the people the loaded log rows actually name — never every project user. */
const projectLogUserOptions = (entries: AuditLogEntry[]): ProjectLogOption[] => {
  const seen = new Map<string, string>()
  entries.forEach((entry) => {
    const { id, label } = resolveLogActor(entry)
    if (id && label && !seen.has(id)) seen.set(id, label)
  })
  return [...seen.entries()]
    .map(([value, label]) => ({ value, label }))
    .sort((a, b) => a.label.localeCompare(b.label))
}

export {
  NO_LOG_FILTERS,
  filterProjectLogs,
  humanizeToken,
  parseLogTimestamp,
  projectLogActionOptions,
  projectLogTypeOptions,
  projectLogUserOptions,
  resolveLogActor,
  sortLogsNewestFirst
}
